import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import {
  LifeBuoy, Send, RefreshCw, Search, User, Shield, Bell, MessageCircle,
  CheckCircle2, XCircle, Clock, Loader2, ImagePlus, Video, X as XIcon,
} from 'lucide-react';

const STATUS_META = {
  pending:     { label_id: 'Menunggu',       label_en: 'Pending',     color: '#F59E0B', Icon: Clock },
  in_progress: { label_id: 'Sedang Diproses', label_en: 'In Progress', color: '#2563EB', Icon: Loader2 },
  done:        { label_id: 'Selesai',        label_en: 'Done',        color: '#16A34A', Icon: CheckCircle2 },
  rejected:    { label_id: 'Ditolak',        label_en: 'Rejected',    color: '#DC2626', Icon: XCircle },
};

const TYPE_LABEL = {
  delete_post:  { id: 'Hapus Postingan',   en: 'Delete Post' },
  delete_drive: { id: 'Hapus Data Drive',  en: 'Delete Drive Data' },
  match_report: { id: 'Lapor Match',       en: 'Match Report' },
  report:       { id: 'Laporkan Masalah',  en: 'Report Misconduct' },
};

const REPORT_CATEGORY_LABEL = {
  harassment: { id: 'Pelecehan', en: 'Harassment' },
  fraud: { id: 'Penipuan', en: 'Fraud / Scam' },
  fake_profile: { id: 'Profil Palsu', en: 'Fake Profile' },
  inappropriate_content: { id: 'Konten Tidak Pantas', en: 'Inappropriate Content' },
  other: { id: 'Lainnya', en: 'Other' },
};

const fmtDT = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminTicketsTab() {
  const { API, authHeaders } = useAuth();
  const { lang } = useLang();
  const isID = lang !== 'en';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const initialMountRef = useRef(true);

  const load = useCallback(async ({ silent = false } = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/tickets`, authHeaders());
      setTickets(data.tickets || []);
    } catch (err) {
      if (!silent && !initialMountRef.current) {
        toast.error(err.response?.data?.detail || (isID ? 'Gagal memuat tiket' : 'Failed to load tickets'));
      }
    }
    setLoading(false);
  }, [API, authHeaders, isID]);

  useEffect(() => {
    load({ silent: true }).finally(() => { initialMountRef.current = false; });
  }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return tickets.filter(t => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (typeFilter !== 'all' && t.ticket_type !== typeFilter) return false;
      if (query) {
        const hay = [
          t.id, t.reporter_name, t.reporter_whatsapp, t.member_code, t.post_link,
          t.match_full_name, t.match_whatsapp,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [tickets, statusFilter, typeFilter, q]);

  const counts = useMemo(() => {
    const c = { all: tickets.length, pending: 0, in_progress: 0, done: 0, rejected: 0 };
    tickets.forEach(t => { if (c[t.status] !== undefined) c[t.status]++; });
    return c;
  }, [tickets]);

  return (
    <div className="space-y-4" data-testid="admin-tickets-tab">
      {/* Header + filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-[#D98A94]" />
          <h3 className="text-lg font-bold t-text">
            {isID ? 'Tiket Support' : 'Support Tickets'}
          </h3>
          <span className="text-xs t-text-muted">
            ({counts.all} {isID ? 'total' : 'total'})
          </span>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="admin-tickets-refresh" className="rounded-full">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          {isID ? 'Muat Ulang' : 'Refresh'}
        </Button>
      </div>

      {/* Status pills */}
      <div className="flex gap-2 flex-wrap">
        {[
          { k: 'all', label: isID ? 'Semua' : 'All', color: '#6B4A50', Icon: MessageCircle },
          ...Object.keys(STATUS_META).map(k => ({ k, label: STATUS_META[k][isID ? 'label_id' : 'label_en'], color: STATUS_META[k].color, Icon: STATUS_META[k].Icon })),
        ].map(({ k, label, color, Icon }) => (
          <button
            key={k}
            onClick={() => setStatusFilter(k)}
            data-testid={`admin-tickets-filter-${k}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${statusFilter === k ? 'text-white shadow-sm' : 'glass-card t-text-secondary hover:border-[#D98A94]'}`}
            style={statusFilter === k ? { background: color, borderColor: color } : {}}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            <span className={`ml-1 text-[10px] px-1.5 rounded-full ${statusFilter === k ? 'bg-white/25' : 'bg-black/5'}`}>
              {counts[k] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* Search + type filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 t-text-muted pointer-events-none" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={isID ? 'Cari ID, nama, WA, kode member...' : 'Search ID, name, WA, member code...'}
            className="pl-9 h-10"
            data-testid="admin-tickets-search"
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="t-bg-input t-border h-10 rounded-lg text-sm t-text px-3"
          data-testid="admin-tickets-type"
        >
          <option value="all">{isID ? 'Semua Jenis' : 'All Types'}</option>
          <option value="delete_post">{TYPE_LABEL.delete_post[lang] || TYPE_LABEL.delete_post.id}</option>
          <option value="delete_drive">{TYPE_LABEL.delete_drive[lang] || TYPE_LABEL.delete_drive.id}</option>
          <option value="report">{TYPE_LABEL.report[lang] || TYPE_LABEL.report.id}</option>
        </select>
      </div>

      {/* Table / list */}
      {loading ? (
        <div className="p-10 text-center t-text-muted">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center t-text-muted glass-card rounded-2xl" data-testid="admin-tickets-empty">
          <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">
            {isID ? 'Belum ada tiket sesuai filter.' : 'No tickets match your filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-tickets-list">
          {filtered.map(t => {
            const meta = STATUS_META[t.status] || STATUS_META.pending;
            const StatusIcon = meta.Icon;
            const typeLabel = TYPE_LABEL[t.ticket_type]?.[lang] || TYPE_LABEL[t.ticket_type]?.id || t.ticket_type;
            const lastMsg = (t.messages || []).slice(-1)[0];
            return (
              <button
                key={t.id}
                onClick={() => setDetail(t)}
                data-testid={`admin-ticket-row-${t.id}`}
                className="w-full text-left glass-card rounded-2xl p-4 hover:border-[#D98A94] transition-all flex items-start gap-3"
              >
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: `${meta.color}22`, color: meta.color }}
                >
                  <StatusIcon className={`w-4 h-4 ${t.status === 'in_progress' ? 'animate-spin' : ''}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${meta.color}18`, color: meta.color }}>
                      {meta[isID ? 'label_id' : 'label_en']}
                    </span>
                    <span className="text-xs t-text-muted">{typeLabel}</span>
                    {t.fee_amount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                        Rp {t.fee_amount.toLocaleString('id-ID')}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 font-semibold t-text truncate">
                    {t.reporter_name} <span className="t-text-muted font-normal">· {t.reporter_whatsapp}</span>
                  </div>
                  <div className="text-xs t-text-muted mt-0.5 truncate">
                    ID: <code>{t.id.slice(0, 8)}…</code> · {fmtDT(t.created_at)}
                  </div>
                  {lastMsg && (
                    <div className="text-xs t-text-secondary mt-1.5 italic truncate">
                      <span className="font-semibold not-italic">{lastMsg.sender === 'admin' ? 'Admin' : lastMsg.sender === 'reporter' ? (isID ? 'Pelapor' : 'Reporter') : 'System'}:</span> {lastMsg.text}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      {detail && (
        <TicketDetailDialog
          ticket={detail}
          onClose={() => setDetail(null)}
          onChanged={(updated) => {
            setTickets(prev => prev.map(x => x.id === updated.id ? updated : x));
            setDetail(updated);
          }}
        />
      )}
    </div>
  );
}

function TicketDetailDialog({ ticket, onClose, onChanged }) {
  const { API, authHeaders } = useAuth();
  const { lang } = useLang();
  const isID = lang !== 'en';
  const [busy, setBusy] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyPhotos, setReplyPhotos] = useState([]);  // [{ path, name, previewUrl }]
  const [replyVideos, setReplyVideos] = useState([]);  // [{ path, name, sizeMB }]
  const [uploading, setUploading] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [pendingStatus, setPendingStatus] = useState(ticket.status);
  const typeLabel = TYPE_LABEL[ticket.ticket_type]?.[lang] || TYPE_LABEL[ticket.ticket_type]?.id || ticket.ticket_type;
  const meta = STATUS_META[ticket.status] || STATUS_META.pending;

  const refetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/tickets`, authHeaders());
      const found = (data.tickets || []).find(x => x.id === ticket.id);
      if (found) onChanged(found);
    } catch { /* noop */ }
  }, [API, authHeaders, ticket.id, onChanged]);

  const updateStatus = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/tickets/${ticket.id}/status`, { status: pendingStatus, admin_note: statusNote }, authHeaders());
      toast.success(isID ? 'Status diperbarui' : 'Status updated');
      setStatusNote('');
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isID ? 'Gagal update status' : 'Failed to update status'));
    }
    setBusy(false);
  };

  const uploadReplyMedia = async (files, kind) => {
    // kind: 'photo' | 'video'
    const isPhoto = kind === 'photo';
    const limitMB = isPhoto ? 30 : 500;
    const maxCount = isPhoto ? 5 : 3;
    const currentCount = isPhoto ? replyPhotos.length : replyVideos.length;
    if (currentCount + files.length > maxCount) {
      toast.error(isID ? `Maksimal ${maxCount} ${isPhoto ? 'foto' : 'video'} per balasan` : `Max ${maxCount} ${isPhoto ? 'photos' : 'videos'} per reply`);
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > limitMB * 1024 * 1024) {
          toast.error(isID ? `${f.name}: melebihi ${limitMB}MB` : `${f.name}: exceeds ${limitMB}MB`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/admin/tickets/${ticket.id}/reply-upload`, fd, {
          ...authHeaders(),
          headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
        });
        const item = { path: res.data.path, name: f.name, sizeMB: (f.size / (1024 * 1024)).toFixed(1) };
        if (isPhoto) {
          item.previewUrl = URL.createObjectURL(f);
          setReplyPhotos(prev => [...prev, item]);
        } else {
          setReplyVideos(prev => [...prev, item]);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isID ? 'Upload gagal' : 'Upload failed'));
    }
    setUploading(false);
  };

  const removePendingMedia = (kind, idx) => {
    if (kind === 'photo') setReplyPhotos(prev => prev.filter((_, i) => i !== idx));
    else setReplyVideos(prev => prev.filter((_, i) => i !== idx));
  };

  const sendReply = async () => {
    const hasMedia = replyPhotos.length > 0 || replyVideos.length > 0;
    if (!replyText.trim() && !hasMedia) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/tickets/${ticket.id}/message`, {
        text: replyText,
        photos: replyPhotos.map(p => p.path),
        videos: replyVideos.map(v => v.path),
      }, authHeaders());
      setReplyText('');
      setReplyPhotos([]);
      setReplyVideos([]);
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isID ? 'Gagal kirim' : 'Failed to send'));
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="admin-ticket-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ background: `${meta.color}18`, color: meta.color }}>
              {meta[isID ? 'label_id' : 'label_en']}
            </span>
            <span>{typeLabel}</span>
            <code className="text-xs t-text-muted font-mono">{ticket.id}</code>
          </DialogTitle>
          <DialogDescription>
            {isID ? 'Detail tiket, ubah status, dan balasan ke pelapor.' : 'Ticket detail, status update, and reply thread.'}
          </DialogDescription>
        </DialogHeader>

        {/* Reporter card */}
        <div className="glass-card rounded-xl p-4 space-y-1.5 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-[#D98A94]" />
            <span className="font-semibold t-text">{isID ? 'Pelapor' : 'Reporter'}</span>
          </div>
          <Row label={isID ? 'Nama' : 'Name'} value={ticket.reporter_name} />
          <Row label="WhatsApp" value={ticket.reporter_whatsapp} />
          <Row label={isID ? 'Diajukan' : 'Submitted'} value={fmtDT(ticket.created_at)} />
        </div>

        {/* Ticket detail */}
        <div className="glass-card rounded-xl p-4 space-y-1.5 text-sm">
          <div className="font-semibold t-text mb-1">{isID ? 'Detail Permintaan' : 'Request Details'}</div>
          {ticket.ticket_type === 'delete_post' && (
            <Row label="Link" value={ticket.post_link} link />
          )}
          {ticket.ticket_type === 'delete_drive' && (
            <>
              <Row label={isID ? 'Kode Member' : 'Member Code'} value={ticket.member_code} />
              <Row label={isID ? 'Nama Cocok' : 'Match Name'} value={ticket.match_full_name} />
              <Row label={isID ? 'WA Cocok' : 'Match WA'} value={ticket.match_whatsapp} />
              <Row label={isID ? 'Domisili' : 'Domicile'} value={ticket.match_domicile} />
              <Row label={isID ? 'No. HP' : 'Phone'} value={ticket.match_phone} />
              <Row label={isID ? 'Pendidikan' : 'Education'} value={ticket.match_education} />
              <Row label={isID ? 'Pekerjaan' : 'Occupation'} value={ticket.match_occupation} />
              <Row label={isID ? 'Dasar' : 'Basis'} value={ticket.reason_basis === 'personal' ? (isID ? 'Alasan Pribadi (Rp 48.000)' : 'Personal (Rp 48,000)') : (isID ? 'Sudah Menikah (gratis)' : 'Married (free)')} />
              {ticket.payment_proof_path && (
                <div className="flex flex-col gap-2 pt-1">
                  <span className="text-xs t-text-muted uppercase tracking-wide">{isID ? 'Bukti Bayar' : 'Payment Proof'}</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <EvidenceThumb path={ticket.payment_proof_path} lang={lang} />
                  </div>
                </div>
              )}
              {ticket.marriage_cert_path && (
                <div className="flex flex-col gap-2 pt-1">
                  <span className="text-xs t-text-muted uppercase tracking-wide">{isID ? 'Bukti Resmi Menikah' : 'Marriage Certificate'}</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <EvidenceThumb path={ticket.marriage_cert_path} lang={lang} />
                  </div>
                </div>
              )}
            </>
          )}
          {ticket.ticket_type === 'report' && (
            <>
              <Row label={isID ? 'Kategori' : 'Category'} value={REPORT_CATEGORY_LABEL[ticket.report_category]?.[lang] || ticket.report_category} />
              <Row label={isID ? 'Nama Oknum' : 'Offender Name'} value={ticket.offender_name} />
              <Row label={isID ? 'Kode Oknum' : 'Offender Code'} value={ticket.offender_code} />
              <Row label={isID ? 'Pernyataan Benar' : 'Truth Declared'} value={ticket.truth_declared ? (isID ? '✅ Ya, dicentang' : '✅ Yes, checked') : (isID ? '❌ Tidak' : '❌ No')} />
              {(ticket.evidence_paths || []).length > 0 && (
                <div className="flex flex-col gap-2 pt-1">
                  <span className="text-xs t-text-muted uppercase tracking-wide">{isID ? 'Bukti' : 'Evidence'} ({ticket.evidence_paths.length})</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {ticket.evidence_paths.map((p) => <EvidenceThumb key={p} path={p} lang={lang} />)}
                  </div>
                </div>
              )}
            </>
          )}
          {ticket.reason && <Row label={isID ? 'Penjelasan' : 'Explanation'} value={ticket.reason} multiline />}
        </div>

        {/* Status controls */}
        <div className="glass-card rounded-xl p-4 space-y-3">
          <div className="font-semibold t-text flex items-center gap-2"><Bell className="w-4 h-4 text-[#D98A94]" />{isID ? 'Ubah Status' : 'Update Status'}</div>
          <div className="flex gap-2 flex-wrap">
            {Object.keys(STATUS_META).map(k => {
              const s = STATUS_META[k];
              const Icon = s.Icon;
              const active = pendingStatus === k;
              return (
                <button
                  key={k}
                  onClick={() => setPendingStatus(k)}
                  data-testid={`admin-ticket-status-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${active ? 'text-white' : 'glass-card t-text-secondary'}`}
                  style={active ? { background: s.color, borderColor: s.color } : {}}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {s[isID ? 'label_id' : 'label_en']}
                </button>
              );
            })}
          </div>
          <Textarea
            value={statusNote}
            onChange={e => setStatusNote(e.target.value)}
            placeholder={isID ? 'Catatan admin (opsional) — akan muncul untuk pelapor' : 'Admin note (optional) — will be shown to reporter'}
            className="min-h-[70px]"
            data-testid="admin-ticket-status-note"
          />
          <Button onClick={updateStatus} disabled={busy || pendingStatus === ticket.status && !statusNote.trim()}
            className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full"
            data-testid="admin-ticket-status-save">
            {isID ? 'Simpan Status & Kirim Update' : 'Save & Push Update'}
          </Button>
        </div>

        {/* Message thread */}
        <div className="glass-card rounded-xl p-4">
          <div className="font-semibold t-text flex items-center gap-2 mb-3">
            <MessageCircle className="w-4 h-4 text-[#D98A94]" />
            {isID ? 'Percakapan' : 'Conversation'}
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1" data-testid="admin-ticket-messages">
            {(ticket.messages || []).length === 0 ? (
              <p className="text-xs t-text-muted italic">{isID ? 'Belum ada pesan.' : 'No messages yet.'}</p>
            ) : (
              ticket.messages.map((m) => <MessageBubble key={m.at + m.sender} msg={m} isID={isID} />)
            )}
          </div>
          <div className="mt-3">
            {ticket.status === 'done' || ticket.status === 'rejected' ? (
              <div className="rounded-lg p-3 text-center text-xs" style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.30)', color: '#16A34A' }} data-testid="admin-ticket-closed-banner">
                {isID
                  ? '✓ Tiket sudah ditandai selesai. Chat sudah ditutup — tidak ada pihak yang bisa mengirim pesan.'
                  : '✓ Ticket marked resolved. Chat is closed — no further messages allowed.'}
              </div>
            ) : (
              <div className="space-y-2">
                {/* Pending media previews */}
                {(replyPhotos.length > 0 || replyVideos.length > 0) && (
                  <div className="flex flex-wrap gap-2 p-2 rounded-lg glass-card">
                    {replyPhotos.map((p, i) => (
                      <div key={p.path} className="relative w-16 h-16 rounded overflow-hidden group" data-testid={`admin-reply-photo-pending-${i}`}>
                        <img src={p.previewUrl} alt={p.name} className="w-full h-full object-cover" />
                        <button onClick={() => removePendingMedia('photo', i)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
                          <XIcon className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                    {replyVideos.map((v, i) => (
                      <div key={v.path} className="relative w-24 h-16 rounded overflow-hidden bg-black flex flex-col items-center justify-center px-1 group" data-testid={`admin-reply-video-pending-${i}`}>
                        <Video className="w-5 h-5 text-white/80" />
                        <div className="text-[9px] text-white/90 truncate max-w-full">{v.name}</div>
                        <div className="text-[8px] text-white/60">{v.sizeMB}MB</div>
                        <button onClick={() => removePendingMedia('video', i)} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5">
                          <XIcon className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                    {uploading && <div className="text-xs t-text-muted self-center">⏳ {isID ? 'Mengupload…' : 'Uploading…'}</div>}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <Textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder={isID ? 'Balasan admin ke pelapor...' : 'Admin reply to reporter...'}
                    className="min-h-[64px] flex-1"
                    data-testid="admin-ticket-reply-input"
                  />
                  <div className="flex flex-col gap-1 self-center">
                    <label className={`w-10 h-10 rounded-full glass-card flex items-center justify-center cursor-pointer hover:border-[#D98A94] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                      title={isID ? `Tambah foto (${replyPhotos.length}/5)` : `Add photos (${replyPhotos.length}/5)`}
                      data-testid="admin-ticket-reply-photo-picker">
                      <ImagePlus className="w-4 h-4 text-[#D98A94]" />
                      <input type="file" accept="image/*" multiple hidden onChange={e => { uploadReplyMedia(Array.from(e.target.files || []), 'photo'); e.target.value = ''; }} />
                    </label>
                    <label className={`w-10 h-10 rounded-full glass-card flex items-center justify-center cursor-pointer hover:border-[#D98A94] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                      title={isID ? `Tambah video (${replyVideos.length}/3)` : `Add videos (${replyVideos.length}/3)`}
                      data-testid="admin-ticket-reply-video-picker">
                      <Video className="w-4 h-4 text-[#D98A94]" />
                      <input type="file" accept="video/*" multiple hidden onChange={e => { uploadReplyMedia(Array.from(e.target.files || []), 'video'); e.target.value = ''; }} />
                    </label>
                  </div>
                  <Button
                    onClick={sendReply}
                    disabled={busy || uploading || (!replyText.trim() && replyPhotos.length === 0 && replyVideos.length === 0)}
                    data-testid="admin-ticket-reply-send"
                    className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-10 self-center"
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />
                    {isID ? 'Kirim' : 'Send'}
                  </Button>
                </div>
                <p className="text-[10px] t-text-muted">
                  {isID
                    ? `Foto max 30MB (${replyPhotos.length}/5) · Video max 500MB (${replyVideos.length}/3)`
                    : `Photos max 30MB (${replyPhotos.length}/5) · Videos max 500MB (${replyVideos.length}/3)`}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EvidenceThumb({ path, lang }) {
  const { API, authHeaders, token } = useAuth();
  const [dataUrl, setDataUrl] = useState('');
  const [contentType, setContentType] = useState('');
  const [err, setErr] = useState(false);
  const isID = lang !== 'en';

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/tickets/evidence/${path}`, {
          ...authHeaders(),
          responseType: 'blob',
        });
        if (!alive) return;
        setContentType(res.data.type || '');
        const url = URL.createObjectURL(res.data);
        setDataUrl(url);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, token]);

  const isPdf = contentType === 'application/pdf' || /\.pdf($|\?)/i.test(path);

  if (err) {
    return <div className="text-[10px] t-text-muted glass-card rounded p-2">{isID ? 'Gagal muat' : 'Load failed'}</div>;
  }
  if (!dataUrl) {
    return <div className="aspect-square rounded glass-card flex items-center justify-center text-[10px] t-text-muted">…</div>;
  }
  if (isPdf) {
    return (
      <a href={dataUrl} target="_blank" rel="noreferrer" className="aspect-square rounded glass-card flex flex-col items-center justify-center hover:border-[#D98A94] transition-colors" data-testid={`evidence-pdf-${path}`}>
        <div className="text-2xl">📄</div>
        <div className="text-[10px] t-text-muted mt-1">PDF · {isID ? 'buka' : 'open'}</div>
      </a>
    );
  }
  return (
    <a href={dataUrl} target="_blank" rel="noreferrer" className="aspect-square rounded overflow-hidden bg-black/5 block" data-testid={`evidence-img-${path}`}>
      <img src={dataUrl} alt="evidence" className="w-full h-full object-cover" />
    </a>
  );
}

function Row({ label, value, link, multiline }) {
  if (!value) return null;
  return (
    <div className={`flex ${multiline ? 'flex-col gap-0.5' : 'gap-2'}`}>
      <span className="text-xs t-text-muted min-w-[110px] uppercase tracking-wide">{label}</span>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-sm text-[#D98A94] underline break-all">{value}</a>
      ) : (
        <span className={`text-sm t-text ${multiline ? '' : 'break-words'}`}>{value}</span>
      )}
    </div>
  );
}

function MessageBubble({ msg, isID }) {
  const isAdmin = msg.sender === 'admin';
  const isReporter = msg.sender === 'reporter';
  const isSystem = msg.sender === 'system';
  const senderLabel = isAdmin ? 'Admin' : isReporter ? (isID ? 'Pelapor' : 'Reporter') : (isID ? 'Sistem' : 'System');
  const SenderIcon = isAdmin ? Shield : isReporter ? User : Bell;
  const API_ROOT = process.env.REACT_APP_BACKEND_URL || '';
  const mediaUrl = (path) => `${API_ROOT}/api/tickets/media/${path}`;
  return (
    <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${isAdmin ? 'bg-[#D98A94] text-white' : isSystem ? 'bg-amber-50 border border-amber-200 text-amber-900' : 'glass-card t-text'}`}>
        <div className="flex items-center gap-1 text-[10px] font-semibold mb-0.5 opacity-80 uppercase tracking-wide">
          <SenderIcon className="w-3 h-3" />
          {senderLabel} · {fmtDT(msg.at)}
        </div>
        {msg.text && <div className="whitespace-pre-wrap break-words">{msg.text}</div>}
        {(msg.photos || []).length > 0 && (
          <div className={`grid gap-1.5 mt-1.5 ${msg.photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {msg.photos.map((p, i) => (
              <a key={p} href={mediaUrl(p)} target="_blank" rel="noreferrer" data-testid={`msg-photo-${i}`}
                className="block rounded-lg overflow-hidden bg-black/10 aspect-square max-h-48">
                <img src={mediaUrl(p)} alt="photo" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        )}
        {(msg.videos || []).length > 0 && (
          <div className="space-y-1.5 mt-1.5">
            {msg.videos.map((v, i) => (
              <video key={v} src={mediaUrl(v)} controls preload="metadata" playsInline
                data-testid={`msg-video-${i}`}
                className="w-full max-w-md rounded-lg bg-black" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
